import { IProduct, IShoppingItem } from '@inter-ikea-kompis/types/lib';
import {
  DEXFPriceUnitTypeCode,
  DEXFSalesPriceTypeCode,
  ExtendedIProduct,
  ImageType,
  Product,
  ProductImageDetails,
  IProductMap,
  ProductDesign,
  ProductType,
  ProductPartial
} from '@inter-ikea-storageone/range';
import { DynamicProduct } from '@inter-ikea-core/core';
import * as R from 'ramda';
import { SortOrder } from 'rangeConfig/@types';
import { ItemTypeEnum } from '@inter-ikea-kompis/enums';

export const getProductImage = (product: Product, imageType: ImageType, index?: number) => {
  return index
    ? R.filter<ProductImageDetails>(R.propEq('type', imageType))(product.images)[index]
    : R.find<ProductImageDetails>(R.propEq('type', imageType))(product.images);
};

export const getLastProductImageOfType = (product: Product, imageType: ImageType) => {
  return R.findLast<ProductImageDetails>(R.propEq('type', imageType))(product.images);
};

export const getQPPImage = (product: Product) => {
  return getProductImage(product, ImageType.QPP);
};

export const getLastQPPImage = (product: Product) => {
  return getLastProductImageOfType(product, ImageType.QPP);
};

export const getMPPImage = (product: Product) => {
  return getProductImage(product, ImageType.MPP);
};

export const getFPPImage = (product: Product) => {
  return getProductImage(product, ImageType.FPP);
};

export const getIPPImage = (product: Product) => {
  return getProductImage(product, ImageType.IPP);
};

export const getDefaultHoverImage = (product: Product) =>
  getFPPImage(product) || getQPPImage(product);

type ProductDescriptionOptions = {
  typeName?: boolean;
  designText?: boolean;
  communicatedSizeText?: boolean;
};

type ShoppingItem = {
  shoppingItem: IShoppingItem;
  product: Product | ProductPartial;
  productsWithThisPart: Product[];
};

/**
 * @returns string based on options passed to function. The order in options will decide the order of the string
 */
export const getProductDescription = (product?: Product, options?: ProductDescriptionOptions) => {
  if (!product) {
    return '';
  }
  if (!options) {
    return product.typeName;
  }
  const optionsLength = Object.values(options).filter(Boolean).length;
  const description = Object.entries(options)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) => value)
    .reduce((acc, [key, value], i) => {
      // @ts-ignore
      if (value && product[key]) {
        // @ts-ignore
        return acc + `${product[key]}${i !== optionsLength - 1 ? ', ' : ''}`;
      }
      return acc;
    }, '');

  return capitalizeFirstLetter(description);
};

export const capitalizeFirstLetter = (str: string) => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return str;
};

export const getAttrInProduct = (path: string, product: Product) =>
  R.path(path.split('.'), product);

export const productsToShoppingItems = (products: Product[]) => {
  if (products.length === 0) {
    return [];
  }
  const shoppingItemMap = products.slice().reduce((acc, product) => {
    return product.parts.reduce((acc, part) => {
      const quantity = acc.get(part.localId)?.shoppingItem.quantity || 0;
      const productsWithThisPart = acc.get(part.localId)?.productsWithThisPart || [];
      acc.set(part.localId, {
        shoppingItem: {
          id: part.localId,
          type: part.itemType,
          quantity: part.complimentary ? quantity - 1 : quantity + 1
        },
        product: part.product || product,
        productsWithThisPart: [...productsWithThisPart, product]
      });
      return acc;
    }, acc);
  }, new Map<string, ShoppingItem>());

  const shoppingItemsArray = Array.from(shoppingItemMap.values()).map(item => {
    const quantity = getQuantity(item);
    const shoppingItem = { ...item.shoppingItem, quantity };
    return { ...item, shoppingItem };
  });
  return shoppingItemsArray.filter(item => item.shoppingItem.quantity > 0);
};

const getQuantity = (item: ShoppingItem) => {
  if (item.product.type === ProductType.PLATSA_SLIDING_DOOR_FRONT) {
    return getPlatsaSlidingDoorFeltQuantity(item);
  }
  return Math.ceil(item.shoppingItem.quantity / item.product.priceInformation.nbrOfItemsInPackage);
};

const sortByHeight = (a: Product, b: Product) => {
  return b.communicatedDimensions.height - a.communicatedDimensions.height;
};

const getPlatsaSlidingDoorFeltQuantity = (item: ShoppingItem) => {
  const feltProductLength = 2400;
  let quantity = 0;
  let feltProducts = [...item.productsWithThisPart].sort(sortByHeight);
  while (feltProducts.length > 0) {
    const productsLeft = [];
    let index = 0;
    let length = 0;
    while (length <= feltProductLength && index < feltProducts.length) {
      if (length + feltProducts[index].communicatedDimensions.height <= feltProductLength) {
        length += feltProducts[index].communicatedDimensions.height;
      } else {
        productsLeft.push(feltProducts[index]);
      }
      index++;
    }
    quantity++;
    feltProducts = productsLeft;
  }
  return quantity;
};

export const getTranslatedTitle = (
  title: string,
  product: Product,
  formatMessage: ({ id }: { id: string }) => string
) => {
  if (title === '') {
    return '';
  }

  const attributes = title.split(/(?=\s)/);
  for (let i = 0; i < attributes.length - 1; i++) {
    if (attributes[i].slice(-1) === ',') {
      attributes[i] = attributes[i].slice(0, -1);
      attributes[i + 1] = ',' + attributes[i + 1];
    }
  }

  const result = attributes.reduce((acc, curr) => {
    const stringToReplace = curr.replaceAll(/[,\s]/g, '');
    if (stringToReplace.startsWith('storageone')) {
      acc = acc + curr.replace(stringToReplace, `${formatMessage({ id: stringToReplace })}`);
    } else {
      const attrInProduct = getAttrInProduct(stringToReplace, product);
      if (attrInProduct) {
        let attrString = attrInProduct as string;
        attrString = `${attrString[0].toLowerCase()}${attrString.slice(1)}`;
        acc = acc + curr.replace(stringToReplace, attrString);
      }
    }
    return acc;
  }, '');
  return capitalizeFirstLetter(result);
};

export const sortProducts = (products: Product[], sortOrder: SortOrder[]): Product[] => {
  return R.sortWith(
    sortOrder.map((s: SortOrder) => {
      const direction = s.reverse ? R.descend : R.ascend;
      return direction(R.path(s.path.split('.')));
    })
  )(products) as Product[];
};

const compareColors = (colorA: string, colorB: string) => {
  const aWhite = colorA.includes('white');
  const bWhite = colorB.includes('white');

  if (aWhite && !bWhite) {
    return -1;
  }
  if (!aWhite && bWhite) {
    return 1;
  }

  const aBlack = colorA.includes('black');
  const bBlack = colorB.includes('black');

  if (aBlack && !bBlack) {
    return 1;
  }
  if (!aBlack && bBlack) {
    return -1;
  }

  return colorA < colorB ? -1 : 1;
};

export const sortColors = (
  colors: ProductDesign[],
  sortFunction: (colorA: string, colorB: string) => 1 | -1 = compareColors
) => {
  return colors.sort(sortFunction);
};

export const createFakeDexfArticle = (articleNbr: string, type: string): ExtendedIProduct => {
  return {
    reason: [],
    itemId: articleNbr,
    valid: true,
    complimentary: true,
    type: type,
    communicatedDimensions: {
      width: 0,
      height: 0,
      depth: 0
    },
    content: {
      itemType: ItemTypeEnum.art,
      itemNoGlobal: articleNbr,
      ruItemNo: articleNbr,
      name: '',
      ruItemType: '',
      typeName: '',
      typeCode: '',
      image: [],
      goodToKnow: [],
      measureReference: { textMetric: '', textImperial: '' },
      measure: [],
      priceInformation: {
        homeDeliveryOnly: true,
        onlineSaleable: false,
        availableForCashCarry: false,
        availableForHomeDelivery: false,
        availableForClickCollect: false,
        fees: [],
        currencyRank: [],
        homeDelivery: {},
        clickCollect: { inRange: true },
        salesPrice: [
          {
            typeCode: DEXFSalesPriceTypeCode.REGULAR_PRICE,
            currencyCode: '',
            priceInclTax: 0,
            priceExclTax: 0,
            indicativeTotalTax: 0,
            changeReason: '',
            validFromDateTime: '',
            validToDateTime: '',
            validFromText: '',
            validToText: ''
          }
        ]
      },
      priceUnit: [
        {
          typeCode: DEXFPriceUnitTypeCode.packageQuantity,
          value: 1,
          text: '',
          textMetric: '',
          textImperial: '',
          factorImperial: 1,
          factorMetric: 1,
          sortNo: 1,
          ptagCode: true,
          typeName: ''
        }
      ],
      validDesignText: '',
      filterAttribute: [],
      customerBenefitSummary: {
        text: '',
        code: ''
      }
    } as any
  };
};

export const dexfArticlesForRangeProducts = (
  rangeProducts: (DynamicProduct & { included?: boolean })[],
  dexfArticleMap: IProductMap
): (ExtendedIProduct | null)[] => {
  return rangeProducts?.reduce<(ExtendedIProduct | null)[]>((acc, product) => {
    if (!product.dexf && !product.included) {
      return acc;
    }

    const art: IProduct = dexfArticleMap[product.dexf!];

    if (art) {
      // If dexf data is missing package quantity information, take it from the range
      const priceUnit = art.content?.priceUnit
        ? art.content?.priceUnit
        : [
            {
              typeCode: DEXFPriceUnitTypeCode.packageQuantity,
              value: product.partsInPackage ? product.partsInPackage : 1
            }
          ];
      return [
        ...acc,
        {
          ...art,
          content: {
            ...art.content,
            priceUnit
          },
          communicatedDimensions: product.template?.comSize,
          type: product.type,
          complimentary: product.included ? product.included : false,
          valid: product.included ? true : art.valid
        } as ExtendedIProduct
      ];
    }
    return product.included
      ? [...acc, createFakeDexfArticle(product.dexf || product.id, product.type!)]
      : [...acc, null];
  }, []);
};

export const getLightWattage = (product: any) => {
  if (product) {
    return product.find((item: any) => item.typeCode === '00025')?.value / 1000;
  }
  return product;
};

export const sortProductsByAscendingPrice = (products: Product[]) => {
  return products.sort((a, b) => a.priceInformation.currentPrice - b.priceInformation.currentPrice);
};
